<template>
  <div v-if="openConversationId && mode !== 'expanded'" class="floating-panel-host">
    <button v-if="mode === 'minimized'" type="button" class="floating-panel-pill" @click="restore">
      <MessageSquare :size="16" /> Conversation <Maximize2 :size="14" />
    </button>

    <section v-else class="floating-panel" aria-label="Conversation">
      <header class="floating-panel__header">
        <ConversationHeaderDetails v-if="conversation" :conversation="conversation" compact class="tw-min-w-0" />
        <div v-else class="floating-panel__title-placeholder">Conversation</div>

        <div class="floating-panel__controls">
          <ConversationActionsMenu v-if="conversation" :conversation="conversation" />
          <button type="button" class="floating-panel__control" title="Expand to full conversation" @click="onExpand">
            <Maximize2 :size="15" />
          </button>
          <button type="button" class="floating-panel__control" title="Minimize" @click="minimize">
            <Minus :size="15" />
          </button>
          <button type="button" class="floating-panel__control" title="Close" @click="closeConversation">
            <X :size="15" />
          </button>
        </div>
      </header>

      <div v-if="isPending" class="floating-panel__hint"><i class="fas fa-spinner fa-spin tw-mr-1" /> Loading…</div>
      <ErrorNotice v-else-if="error" :error-message="describePatientConversationError(error)" class="tw-m-4" />

      <template v-else-if="conversation">
        <ConversationMessageList
          :patient-conversation-id="conversation.id"
          :patient-name="conversation.member?.full_name ?? 'the patient'"
          compact
        />
        <ConversationComposer :key="conversation.id" :conversation="conversation" />
      </template>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router"
import { Maximize2, MessageSquare, Minus, X } from "lucide-vue-next"
import ErrorNotice from "@/components/ErrorNotice.vue"
import ConversationActionsMenu from "@/modules/patient-conversations/components/thread/ConversationActionsMenu.vue"
import ConversationComposer from "@/modules/patient-conversations/components/thread/ConversationComposer.vue"
import ConversationHeaderDetails from "@/modules/patient-conversations/components/thread/ConversationHeaderDetails.vue"
import ConversationMessageList from "@/modules/patient-conversations/components/thread/ConversationMessageList.vue"
import { usePatientConversationPanel } from "@/modules/patient-conversations/composables/patient-conversation-panel"
import { usePatientConversation } from "@/modules/patient-conversations/composables/patient-conversations"
import { describePatientConversationError } from "@/modules/patient-conversations/constants/patient-conversation-error-codes"

const INBOX_ROUTE_NAME = "patient-conversations-inbox"

const route = useRoute()
const router = useRouter()
const { openConversationId, mode, minimize, restore, expand, closeConversation } = usePatientConversationPanel()
const { data: conversation, isPending, error } = usePatientConversation(openConversationId)

// The full thread lives in the inbox page, so expanding from the chart first goes there
function onExpand(): void {
  expand()
  if (route.name !== INBOX_ROUTE_NAME) router.push({ name: INBOX_ROUTE_NAME })
}
</script>

<style scoped lang="sass">
@import "@/styles/colors"

// Above the page, below Element Plus overlays (2000+) so pickers opened from the panel stay on top
.floating-panel-host
  position: fixed
  right: 20px
  bottom: 20px
  z-index: 1800

.floating-panel
  display: flex
  flex-direction: column
  width: 420px
  max-width: calc(100vw - 40px)
  height: 620px
  max-height: calc(100vh - 100px)
  background-color: white
  border: 1px solid $border
  border-radius: 12px
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15)
  overflow: hidden

  &__header
    display: flex
    align-items: flex-start
    justify-content: space-between
    gap: 12px
    padding: 14px 16px
    border-bottom: 1px solid $border

  &__title-placeholder
    font-size: 15px
    font-weight: 700
    color: $empty-text

  &__controls
    display: flex
    flex: none
    gap: 2px

  &__control
    display: inline-flex
    align-items: center
    justify-content: center
    width: 28px
    height: 28px
    border: 0
    border-radius: 6px
    background: transparent
    color: $grey
    cursor: pointer

    &:hover
      background-color: $grey-light
      color: $empty-text

  &__hint
    padding: 32px 0
    text-align: center
    font-size: 13px
    color: $grey

.floating-panel-pill
  display: inline-flex
  align-items: center
  gap: 8px
  padding: 12px 18px
  border: 0
  border-radius: 9999px
  background-color: $primary
  color: white
  font-size: 14px
  font-weight: 600
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18)
  cursor: pointer

  &:hover
    background-color: $highlight-dark
</style>
